import { WebSocketService } from '@aimsos/websocket';

const VALID_SCOPES = ['NOTIFICATION', 'CONVERSATION'];

/**
 * Bridges `@aimsos/websocket` `WebSocketService` to the ann-web-app-style event bus
 * (`on('scope:CONVERSATION', handler)`), matching what `@aimsos/chat` streaming expects.
 */
export class ChatWebSocketFacade {
  constructor() {
    this.inner = new WebSocketService();
    this.listeners = new Map();
    this._onRawMessage = this._onRawMessage.bind(this);
    this.inner.on('message', this._onRawMessage);
  }

  configure(config) {
    this.inner.configure(config);
  }

  connect() {
    this.inner.connect();
  }

  disconnect() {
    this.inner.disconnect();
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (!this.listeners.has(event)) return;
    const callbacks = this.listeners.get(event);
    const index = callbacks.indexOf(callback);
    if (index > -1) callbacks.splice(index, 1);
  }

  emit(event, data) {
    if (!this.listeners.has(event)) return;
    this.listeners.get(event).forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`[ChatWebSocketFacade] Error in listener for ${event}`, error);
      }
    });
  }

  isValidMessage(data) {
    if (
      !data ||
      typeof data !== 'object' ||
      !data.scope ||
      !data.event ||
      typeof data.scope !== 'string' ||
      typeof data.event !== 'string'
    ) {
      return false;
    }
    const scopeUpper = String(data.scope ?? '').toUpperCase();
    if (!VALID_SCOPES.includes(scopeUpper)) {
      return false;
    }
    return true;
  }

  _onRawMessage(rawData) {
    let messageData = rawData;
    if (rawData && rawData.data && typeof rawData.data === 'object') {
      if (rawData.data.scope && rawData.data.event) {
        messageData = rawData.data;
        if (rawData.timestamp && !messageData.timestamp) {
          messageData.timestamp = rawData.timestamp;
        }
      }
    }

    if (!this.isValidMessage(messageData)) {
      // Intentionally kept: silent drops here once cost a full day of diagnosis.
      // One quiet line naming the dropped frame is cheap insurance; debug level
      // keeps it out of default consoles.
      console.debug(
        '[ChatWebSocketFacade] Dropped frame failing isValidMessage',
        { type: messageData?.type, event: messageData?.event },
      );
      return;
    }

    const {
      scope,
      event,
      data: payloadData,
      action,
      payload,
      metadata,
      id,
      ref_id: dataRefId,
      timestamp,
      sequence,
    } = messageData;
    const ref_id = dataRefId ?? rawData?.ref_id;
    const normalizedScope = String(scope ?? '').toUpperCase();
    const finalPayload = payloadData || payload || {};
    const normalizedMessage = {
      id,
      scope: normalizedScope,
      event,
      action,
      payload: finalPayload,
      metadata: metadata || {},
      ref_id,
      sequence,
      timestamp: timestamp || rawData.timestamp,
    };

    this.emit(`scope:${normalizedScope}`, normalizedMessage);
    this.emit(`scope:${normalizedScope}:${event}`, normalizedMessage);
    this.emit(`event:${event}`, normalizedMessage);
  }
}
